import type React from "react";

import { useNavigate } from "@tanstack/react-router";

import type { ViewLeadDetailsResponse } from "@pbz/schema/lead";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Loader,
} from "@pbz/ui-kit";

import { usePermissionCheck } from "@/hooks/usePermissionCheck";
import { useGetLeadView } from "@/queries/lead";

interface ViewLeadProps {
  leadId: number;
  onLeadUpdate?: () => void;
  onClose?: () => void;
}

const ViewLead: React.FC<ViewLeadProps> = ({ leadId, onClose }) => {
  const navigate = useNavigate();
  const { canUpdateLead } = usePermissionCheck();

  // Fetch lead details
  const { data: leadData, isLoading: isLoadingLeadDetails } = useGetLeadView(
    { leadId },
    true, // Always fetch when component is rendered
  );

  const leadDetails = leadData?.data as ViewLeadDetailsResponse | undefined;

  const closeModal = () => {
    onClose?.(); // Call the callback to notify parent component
  };

  const handleUpdate = () => {
    navigate({
      to: "/lead/edit/$leadId",
      params: { leadId: leadId.toString() },
    });
  };

  // Helper function to format field value
  const formatFieldValue = (
    value: string | number | boolean | null | undefined,
  ) => {
    if (value === null || value === undefined) {
      return "-";
    }
    if (typeof value === "boolean") {
      return value ? "Yes" : "No";
    }
    return value.toString();
  };

  return (
    <Dialog onOpenChange={closeModal} open={true}>
      <DialogContent className="max-w-8xl mx-auto">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-gray-900">
            View Details
          </DialogTitle>
          <div className="border-b border-gray-200 mt-4" />
        </DialogHeader>

        {isLoadingLeadDetails && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <Loader className="h-8 w-8" />
            <div className="text-sm text-gray-500">Loading lead details...</div>
          </div>
        )}
        {!isLoadingLeadDetails && leadDetails && (
          <div className="space-y-6">
            {leadDetails.map(
              (section) =>
                section.Rows &&
                section.Rows.length > 0 && (
                  <div key={section.Header}>
                    <h3 className="text-lg font-semibold text-blue-600 mb-4 underline">
                      {section.Header}
                    </h3>
                    <div className=" p-4">
                      <div className="grid grid-cols-4 gap-6">
                        {section.Rows.map((row) =>
                          row.Fields.map((field) => {
                            const displayValue = formatFieldValue(field.Value);

                            return (
                              <div
                                className="min-w-0"
                                key={`${field.Name}-${field.Value}`}
                              >
                                <p className="text-xs text-gray-500 mb-1 truncate">
                                  {field.Name}:
                                </p>
                                <p className="text-sm text-gray-900 break-words overflow-wrap-anywhere">
                                  {displayValue}
                                </p>
                              </div>
                            );
                          }),
                        )}
                      </div>
                    </div>
                  </div>
                ),
            )}
          </div>
        )}
        {!isLoadingLeadDetails && leadDetails === undefined && (
          <div className="flex flex-col items-center justify-center py-8 gap-2">
            <div className="text-sm text-gray-500">No lead data found</div>
          </div>
        )}

        <div className="border-b border-gray-200 mt-6 w-full" />

        <DialogFooter className="mt-6">
          <div className="flex gap-3 justify-end">
            <Button
              className="bg-white text-gray-700 hover:bg-gray-50 border border-gray-300"
              onClick={closeModal}
              variant="outline"
            >
              Cancel
            </Button>
            {canUpdateLead() && (
              <Button
                className="bg-blue-600 hover:bg-blue-700 text-white"
                onClick={handleUpdate}
              >
                Update
              </Button>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ViewLead;
